<template>
  <div>
    <div class="panel-heading">
      <h4 class="panel-title">{{ $t('admin.activityLogTitle') }}</h4>
    </div>

    <div
      class="panel-body"
      style="display: block;"
    >
      <div class="row activity-log-filters">
        <div class="col-sm-6 col-md-3">
          <label>{{ $t('columns.fromDate') }}</label>
          <input
            v-model="filters.from_date"
            type="date"
            class="form-control"
            @change="applyFilters"
          />
        </div>
        <div class="col-sm-6 col-md-3">
          <label>{{ $t('columns.toDate') }}</label>
          <input
            v-model="filters.to_date"
            type="date"
            class="form-control"
            @change="applyFilters"
          />
        </div>
        <div class="col-sm-6 col-md-2">
          <label>{{ $t('activityLog.accountTypeLabel') }}</label>
          <select
            v-model="filters.causer_role"
            class="form-control"
            @change="applyFilters"
          >
            <option value="">{{ $t('activityLog.allAccountsOption') }}</option>
            <option value="staff">{{ $t('user.staffTitle') }}</option>
            <option value="customer">{{ $t('columns.customer') }}</option>
          </select>
        </div>
        <div class="col-sm-6 col-md-2">
          <label>{{ $t('columns.platform') }}</label>
          <select
            v-model="filters.platform"
            class="form-control"
            @change="applyFilters"
          >
            <option value="">{{ $t('activityLog.allPlatformsOption') }}</option>
            <option value="admin-web">{{ $t('activityLog.adminWebOption') }}</option>
            <option value="mobile-ios">{{ $t('activityLog.mobileIosOption') }}</option>
            <option value="mobile-android">{{ $t('activityLog.mobileAndroidOption') }}</option>
          </select>
        </div>
        <div class="col-sm-6 col-md-2 text-right">
          <label class="d-block">&nbsp;</label>
          <button
            class="btn btn-default"
            @click="resetFilters"
          >{{ $t('common.reset') }}</button>
        </div>
      </div>

      <div class="row activity-log-filters">
        <div class="col-sm-6 col-md-4 activity-log-actor-search">
          <label>{{ $t('activityLog.accountNameLabel') }}</label>
          <input
            v-model="actorSearch"
            type="text"
            class="form-control"
            :placeholder="$t('activityLog.searchStaffOrCustomerPlaceholder')"
            @input="onActorSearch"
          />
          <ul
            v-if="actorOptions.length > 0"
            class="activity-log-actor-suggestions"
          >
            <li
              v-for="actor in actorOptions"
              :key="actor.user_id"
              @click="selectActor(actor)"
            >
              {{ actor.full_name }}
              <span class="label" :class="actor.is_staff ? 'bg-primary-600' : 'bg-warning-300'">
                {{ actor.is_staff ? 'staff' : 'customer' }}
              </span>
            </li>
          </ul>
        </div>
        <div class="col-sm-6 col-md-3">
          <label>{{ $t('activityLog.eventLabel') }}</label>
          <input
            v-model="filters.event"
            type="text"
            class="form-control"
            :placeholder="$t('activityLog.eventPlaceholder')"
            @change="applyFilters"
          />
        </div>
        <div class="col-sm-6 col-md-3">
          <label>{{ $t('activityLog.subjectTypeLabel') }}</label>
          <input
            v-model="filters.subject_type"
            type="text"
            class="form-control"
            :placeholder="$t('activityLog.subjectTypePlaceholder')"
            @change="applyFilters"
          />
        </div>
        <div class="col-sm-6 col-md-2">
          <label>{{ $t('common.search') }}</label>
          <input
            v-model="filters.search"
            type="text"
            class="form-control"
            :placeholder="$t('activityLog.routeOrDescriptionPlaceholder')"
            @keyup.enter="applyFilters"
          />
        </div>
      </div>

      <div
        v-if="filters.causer_id"
        class="row"
        style="margin-top: 10px;"
      >
        <div class="col-xs-12">
          <span class="label bg-primary-600">
            {{ $t('activityLog.showingHistoryForPrefix') }} {{ filters.causer_name }}
            <a @click="clearActorFilter"><i class="fa fa-times"></i></a>
          </span>
        </div>
      </div>

      <div class="col-xs-12" style="padding-top: 15px;">
        <table class="table table-striped">
          <thead>
            <tr>
              <th>{{ $t('columns.dateTime') }}</th>
              <th>{{ $t('activityLog.accountColumn') }}</th>
              <th>{{ $t('columns.note') }}</th>
              <th>{{ $t('activityLog.eventLabel') }}</th>
              <th>{{ $t('columns.platform') }}</th>
              <th>{{ $t('columns.status') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="log in apiResults">
              <tr :key="log.id">
                <td>{{ log.created_at }}</td>
                <td>
                  <a
                    v-if="log.causer"
                    @click="filterByActor(log)"
                  >{{ log.causer.full_name }}</a>
                  <span v-else>{{ $t('activityLog.guestLabel') }}</span>
                  <span
                    v-if="log.causer_role"
                    class="label"
                    :class="log.causer_role === 'staff' ? 'bg-primary-600' : 'bg-warning-300'"
                  >{{ log.causer_role }}</span>
                </td>
                <td>{{ log.note || log.description || log.route }}</td>
                <td>
                  <span class="label bg-indigo-600">{{ log.event }}</span>
                </td>
                <td>{{ log.platform }}</td>
                <td>
                  <span
                    class="label"
                    :class="log.status_code && log.status_code < 400 ? 'bg-green-600' : 'bg-danger-600'"
                  >{{ log.status_code }}</span>
                </td>
              </tr>
            </template>
            <tr v-if="apiResults.length === 0">
              <td
                colspan="6"
                class="text-center"
              >{{ $t('common.noResultsFound') }}</td>
            </tr>
          </tbody>
        </table>

        <hr />
        <div class="row">
          <div class="col-xs-12 text-right">
            <paginate
              :page-count="pagination.lastPage"
              :page-range="2"
              :changePage="changePage"
              :container-class="'pagination'"
              :pagination="pagination"
              :page-class="'page-item'"
            >
            </paginate>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationVue from './Pagination.vue'
import axios from 'axios'

export default {
  components: {
    paginate: PaginationVue
  },
  data() {
    return {
      url: 'api/activity-logs',
      apiResults: [],
      actorSearch: '',
      actorOptions: [],
      actorSearchTimeout: null,
      filters: {
        per_page: 15,
        from_date: '',
        to_date: '',
        causer_role: '',
        causer_id: '',
        causer_name: '',
        platform: '',
        event: '',
        subject_type: '',
        search: ''
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: ''
      }
    }
  },
  mounted() {
    this.getResults()
  },
  methods: {
    authHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('access_token') }
    },

    getResults(url = this.url) {
      axios
        .get(url, { params: this.filters, headers: this.authHeaders() })
        .then(response => {
          const data = response.data.data
          this.apiResults = data.data
          this.configPagination(data)
        })
        .catch(errors => { console.error(errors) })
    },

    applyFilters() {
      this.url = 'api/activity-logs'
      this.getResults()
    },

    resetFilters() {
      this.filters = {
        per_page: 15,
        from_date: '',
        to_date: '',
        causer_role: '',
        causer_id: '',
        causer_name: '',
        platform: '',
        event: '',
        subject_type: '',
        search: ''
      }
      this.actorSearch = ''
      this.actorOptions = []
      this.applyFilters()
    },

    onActorSearch() {
      clearTimeout(this.actorSearchTimeout)
      if (this.actorSearch.trim().length < 2) {
        this.actorOptions = []
        return
      }
      this.actorSearchTimeout = setTimeout(() => {
        axios
          .get('api/activity-logs/actors', { params: { search: this.actorSearch }, headers: this.authHeaders() })
          .then(response => {
            this.actorOptions = response.data.data
          })
          .catch(errors => { console.error(errors) })
      }, 300)
    },

    selectActor(actor) {
      this.filters.causer_id = actor.user_id
      this.filters.causer_name = actor.full_name
      this.actorSearch = actor.full_name
      this.actorOptions = []
      this.applyFilters()
    },

    filterByActor(log) {
      if (!log.causer) {
        return
      }
      this.selectActor(log.causer)
    },

    clearActorFilter() {
      this.filters.causer_id = ''
      this.filters.causer_name = ''
      this.actorSearch = ''
      this.applyFilters()
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults(pageUrl)
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    }
  }
}
</script>

<style>
.activity-log-filters {
  margin-bottom: 10px;
}

.activity-log-filters label {
  font-weight: 600;
  margin-bottom: 2px;
}

.activity-log-actor-search {
  position: relative;
}

.activity-log-actor-suggestions {
  position: absolute;
  z-index: 20;
  background: #fff;
  border: 1px solid #ddd;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.activity-log-actor-suggestions li {
  padding: 6px 10px;
  cursor: pointer;
}

.activity-log-actor-suggestions li:hover {
  background: #f5f5f5;
}
</style>



// WEBPACK FOOTER //
// src/components/activityLog.vue